import { useTranslation } from "react-i18next";
import { HelpLink } from "./HelpLink";
import { SettingsGroup, SettingsSection } from "./SettingsSection";
import { LevelChoice } from "./LevelChoice";
import { useLevels } from "./levels";
import { LanguageSwitch } from "./LanguageSwitch";
import { useCoreDoc } from "./useCoreDoc";
import { setSetting, useSettings } from "./settings";
import { useSession } from "./session";
import { Checkbox, NumberInput, Select, ConfirmButton } from "../ui";
import { purgeThreads } from "../assistant/client";
import { AccessSettings } from "./AccessSettings";
import { AutomationsSettings } from "./AutomationsSettings";
import { CalendarsSettings, TimeZoneSettings } from "./CalendarsSettings";
import { BundlesSettings } from "./BundlesSettings";
import { DataSettings } from "./DataSettings";
import { IntegrationsSettings } from "./IntegrationsSettings";
import { CircleSettings, PresenceSection } from "../circle/CircleSettings";
import { FairnessSettings } from "../circle/FairnessSettings";
import { MyProfileSettings } from "../circle/PeopleSettings";
import { DeviceSettings } from "../circle/DeviceSettings";
import { MailInSettings } from "../mailin/MailInSettings";
import { PluginSettings } from "./PluginSettings";
import { AssistantModelSettings, BackupEmailSettings, SecretsSettings } from "./ServerSettings";
import { NotificationSettings } from "./NotificationSettings";
import { CaptureSettings } from "./CaptureSettings";
import { CalendarFeedSettings } from "./CalendarFeedSettings";
import { ImportSettings } from "./ImportSettings";
import { ButtonsSettings } from "./ButtonsSettings";
import { PlacesSettings, SignalsSettings } from "../signals/SignalsSettings";
import { useReadOnly } from "./useReadOnly";
import { useComfort, type Comfort } from "./comfort";
import { Fold } from "../ui/Fold";
import type { Level } from "../core/schema";

/**
 * Settings (spec §9.24), grouped by who a setting affects (UI cleanup U5, ADR 0032): *Just me* (the person, on
 * every device), *This device*, *Everyone in this workspace*, and *The server* (admins). The level — how much Drang
 * shows — only shows or hides, never changes data. Everything here is `simple` unless marked.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const { handle, workspaceId } = useCoreDoc();
  const s = useSettings(handle);
  const comfort = useComfort();
  const levels = useLevels();
  const level = levels.settings;
  const profile = useSession((st) => st.session?.profile);
  const kid = profile?.kind === "kid";
  const ro = useReadOnly(handle, workspaceId);
  const set = (path: string, v: unknown) => handle && !ro && setSetting(handle.doc, path, v);

  return (
    <div
      className="mx-auto w-full max-w-4xl px-4 pb-16 pt-5 mid:px-9 mid:pt-8"
      data-testid="settings"
      data-level={level}
    >
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <h1 className="text-[26px] font-semibold">{t("nav.settings")}</h1>
        <HelpLink page="guides/settings/" />
        {/* how much Drang shows (spec §9.24, ADR 0019): per person, everywhere; only shows or hides */}
        <LevelChoice
          value={level}
          onChange={(l) => void levels.set("settings", l)}
          label={t("settings.levelLabel")}
          testId="level"
          disabled={kid}
        />
      </div>
      <p className="mt-1 text-[12.5px] text-neutral-500">{t("settings.levelHint")}</p>

      <SettingsGroup
        title={t("settings.groups.me")}
        hint={t("settings.groups.meHint")}
        testId="settings-group-me"
      >
        <SettingsSection
          id="settings-language"
          intro={t("settings.intro.language")}
          title={t("language.label")}
          testId="settings-language"
          control={<LanguageSwitch />}
        />
        <SettingsSection
          id="settings-a11y"
          intro={t("settings.intro.feel")}
          title={t("settings.feel")}
          testId="settings-a11y"
        >
          <label className="flex items-center gap-3 text-sm">
            <span className="w-32 shrink-0 text-[12.5px] text-neutral-500">{t("settings.theme")}</span>
            <Select
              value={comfort.theme}
              onChange={(e) => void comfort.set({ theme: e.target.value as Comfort["theme"] })}
              aria-label={t("settings.theme")}
              data-testid="theme"
            >
              <option value="system">{t("settings.themeSystem")}</option>
              <option value="light">{t("settings.themeLight")}</option>
              <option value="dark">{t("settings.themeDark")}</option>
            </Select>
          </label>
          <label className="flex items-center gap-3 text-sm">
            <span className="w-32 shrink-0 text-[12.5px] text-neutral-500">{t("settings.quickAddBar")}</span>
            <Select
              value={comfort.quickAddBar}
              onChange={(e) => void comfort.set({ quickAddBar: e.target.value as Comfort["quickAddBar"] })}
              aria-label={t("settings.quickAddBar")}
              data-testid="quick-add-bar"
            >
              <option value="off">{t("settings.quickAddBarOff")}</option>
              <option value="top">{t("settings.quickAddBarTop")}</option>
              <option value="bottom">{t("settings.quickAddBarBottom")}</option>
            </Select>
          </label>
          {(["reducedMotion", "muteBadges", "plainColors", "widgetLines", "showOpenCount"] as const).map(
            (k) => (
              <label key={k} className="flex items-center gap-3 text-sm" data-testid={`comfort-${k}`}>
                <Checkbox
                  checked={comfort[k]}
                  label={t(`settings.a11y.${k}`)}
                  onChange={(v) => void comfort.set({ [k]: v })}
                />
                <span>{t(`settings.a11y.${k}`)}</span>
                <span className="text-[12px] text-neutral-500">{t(`settings.a11y.${k}Hint`)}</span>
              </label>
            ),
          )}
          {/* the task panel's own level: an override, under Details (UI cleanup U5) */}
          <div className="flex flex-col gap-1" data-testid="panel-level">
            <Fold
              label={t("settings.panelLevelDetails")}
              defaultOpen={levels.panelOwn != null}
              toggleTestId="panel-level-toggle"
            >
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <span className="text-[12.5px] text-neutral-500">{t("settings.panelLevelLabel")}</span>
                <Select
                  value={levels.panelOwn ?? ""}
                  disabled={kid}
                  onChange={(e) => void levels.set("panel", (e.target.value || null) as Level | null)}
                  aria-label={t("settings.panelLevelLabel")}
                  data-testid="panel-level-select"
                >
                  <option value="">{t("settings.panelLevelSame")}</option>
                  <option value="simple">{t("settings.level.simple")}</option>
                  <option value="standard">{t("settings.level.standard")}</option>
                  <option value="power">{t("settings.level.power")}</option>
                </Select>
              </div>
              <p className="text-[12px] text-neutral-500">{t("settings.panelLevelHint")}</p>
            </Fold>
          </div>
        </SettingsSection>
        <MyProfileSettings />
        {level !== "simple" && <NotificationSettings />}
        {level !== "simple" && <PlacesSettings scope="mine" />}
        {level !== "simple" && <PresenceSection />}
        {level !== "simple" && <CaptureSettings />}
        {level !== "simple" && <CalendarFeedSettings />}
        {level === "power" && <AccessSettings />}
      </SettingsGroup>

      <SettingsGroup
        title={t("settings.groups.device")}
        hint={t("settings.groups.deviceHint")}
        testId="settings-group-device"
      >
        <DeviceSettings />
      </SettingsGroup>

      <SettingsGroup
        title={t("settings.groups.workspace")}
        hint={t("settings.groups.workspaceHint")}
        testId="settings-group-workspace"
      >
        <CircleSettings />
        <FairnessSettings />
        {level !== "simple" && (
          <SettingsSection
            id="settings-standard"
            intro={t("settings.intro.pomodoro")}
            title={t("settings.pomodoro")}
            testId="settings-standard"
          >
            <div className="flex flex-wrap gap-4 text-sm">
              {(["workMin", "shortMin", "longMin"] as const).map((k) => (
                <label key={k} className="flex items-center gap-2">
                  <span className="text-[12.5px] text-neutral-500">{t(`settings.pomodoro_${k}`)}</span>
                  <NumberInput
                    min={1}
                    max={180}
                    className="kss-control kss-field h-9 w-20 px-2 text-sm"
                    value={s.pomodoro[k]}
                    disabled={ro}
                    onCommit={(n) => set(`pomodoro.${k}`, n)}
                    aria-label={t(`settings.pomodoro_${k}`)}
                  />
                </label>
              ))}
              {/* how often the long break comes: every N breaks (= work cycles), right after "Long break" */}
              <label className="flex items-center gap-2" data-testid="pomodoro-every">
                <span className="text-[12.5px] text-neutral-500">{t("settings.pomodoro_every")}</span>
                <NumberInput
                  min={2}
                  max={12}
                  className="kss-control kss-field h-9 w-16 px-2 text-sm"
                  value={s.pomodoro.cyclesBeforeLong}
                  disabled={ro}
                  onCommit={(n) => set("pomodoro.cyclesBeforeLong", n)}
                  aria-label={t("settings.pomodoro_everyLabel")}
                />
                <span className="text-[12.5px] text-neutral-500">{t("settings.pomodoro_breaks")}</span>
              </label>
            </div>
            {(["autoPauseInBreaks", "linkPauses"] as const).map((k) => (
              <label key={k} className="flex items-center gap-3 text-sm" data-testid={`pomodoro-${k}`}>
                <Checkbox
                  checked={s.pomodoro[k] === true}
                  disabled={ro}
                  label={t(`settings.pomodoro_${k}`)}
                  onChange={(v) => set(`pomodoro.${k}`, v)}
                />
                <span>{t(`settings.pomodoro_${k}`)}</span>
                <span className="text-[12px] text-neutral-500">{t(`settings.pomodoro_${k}Hint`)}</span>
              </label>
            ))}
            {/* break reminders and the idle question had no control (UI cleanup U5); 0 switches them off */}
            <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="breaks">
              <span className="text-[12.5px] text-neutral-500">{t("settings.breaks_every")}</span>
              <NumberInput
                min={0}
                max={480}
                className="kss-control kss-field h-9 w-20 px-2 text-sm"
                value={s.breaks.everyMin}
                disabled={ro}
                onCommit={(n) => set("breaks.everyMin", n)}
                aria-label={t("settings.breaks_every")}
              />
              <span className="text-[12.5px] text-neutral-500">{t("settings.breaks_length")}</span>
              <NumberInput
                min={1}
                max={60}
                className="kss-control kss-field h-9 w-16 px-2 text-sm"
                value={s.breaks.lengthMin}
                disabled={ro}
                onCommit={(n) => set("breaks.lengthMin", n)}
                aria-label={t("settings.breaks_length")}
              />
              <span className="text-[12px] text-neutral-500">{t("settings.breaks_hint")}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="idle">
              <span className="text-[12.5px] text-neutral-500">{t("settings.idle_after")}</span>
              <NumberInput
                min={0}
                max={240}
                className="kss-control kss-field h-9 w-20 px-2 text-sm"
                value={s.idle.promptAfterMin}
                disabled={ro}
                onCommit={(n) => set("idle.promptAfterMin", n)}
                aria-label={t("settings.idle_after")}
              />
              <span className="text-[12px] text-neutral-500">{t("settings.idle_hint")}</span>
            </div>
          </SettingsSection>
        )}
        {level !== "simple" && <TimeZoneSettings handle={handle} />}
        {level !== "simple" && <CalendarsSettings handle={handle} />}
        {level !== "simple" && <SignalsSettings />}
        {level !== "simple" && <PlacesSettings scope="shared" />}
        {level !== "simple" && <ButtonsSettings />}
        {level === "power" && <AutomationsSettings />}
        {level === "power" && <MailInSettings />}
        {level !== "simple" && (
          <SettingsSection
            id="settings-assistant"
            intro={t("assistant.settingsHint")}
            summary={t("settings.summary.assistant")}
            title={t("assistant.title")}
            testId="settings-assistant"
          >
            {level === "power" && (
              <label className="flex items-center gap-3 text-sm">
                <Checkbox
                  checked={s.assistant.autoApply}
                  disabled={ro}
                  label={t("assistant.autoApply")}
                  onChange={(v) => set("assistant.autoApply", v)}
                />
                <span>{t("assistant.autoApply")}</span>
              </label>
            )}
            <div>
              <ConfirmButton
                size="sm"
                variant="secondary"
                disabled={!workspaceId}
                question={t("assistant.purgeConfirm")}
                onConfirm={() => workspaceId && void purgeThreads(workspaceId)}
                testId="assistant-purge"
              >
                {t("assistant.purge")}
              </ConfirmButton>
            </div>
          </SettingsSection>
        )}
        {level === "power" && (
          <SettingsSection
            id="settings-graphs"
            intro={t("settings.graphRendererHint")}
            title={t("settings.graphRenderer")}
            testId="settings-graphs"
            control={
              <Select
                value={s.graph.renderer}
                disabled={ro}
                onChange={(e) => set("graph.renderer", e.target.value)}
                aria-label={t("settings.graphRenderer")}
                data-testid="graph-renderer"
              >
                <option value="cytoscape">Cytoscape</option>
                <option value="reactflow">React Flow</option>
              </Select>
            }
          />
        )}
        {/* Move data: this workspace out and in, from another app, and bundles — side by side (UI cleanup U5) */}
        <DataSettings />
        {level !== "simple" && <ImportSettings />}
        {level === "power" && <BundlesSettings />}
      </SettingsGroup>

      {profile?.isAdmin && (
        <SettingsGroup
          title={t("settings.groups.server")}
          hint={t("settings.groups.serverHint")}
          testId="settings-group-server"
        >
          {level !== "simple" && <AssistantModelSettings />}
          {level !== "simple" && <BackupEmailSettings />}
          {level === "power" && <SecretsSettings />}
          {level === "power" && <IntegrationsSettings />}
          {level === "power" && <PluginSettings />}
        </SettingsGroup>
      )}
    </div>
  );
}
